import { Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core'
import { Subscription } from 'rxjs'
import { GuideService, IGuideStep } from '../../services/guide.service'
import { resolveTourTarget } from '../../utils/tour-target.util'

/**
 * Floating, frosted, draggable tour card — the guided walkthrough's narration surface. It renders
 * whenever `GuideService.active$` is true, showing the current step's title + prose, Back/Next (Done on
 * the last step), and step dots. The walk neither mentions nor links to the assistant — the tour ships
 * independently of the assistant dark-launch flag.
 *
 * design-contract.md → §Surface 3.2: narration placement is FIXED. The card is parked in the
 * bottom-right corner and stays there for every step, including the intro — it is `<tour-indicator>`
 * that travels to each step's subject. The only thing that repositions the card is the user dragging it
 * by its header (the pointer-move/up pattern mirrors `<assistant-terminal>`'s resize grip, clamped to
 * the viewport).
 *
 * Frosted: a mostly-opaque white background + a soft backdrop blur so the prose stays crisp, with a soft
 * shadow and rounded corners. Reduced-motion friendly (no entrance animation). Keyboard-operable: every
 * control is focusable, the card takes focus as each step arrives so Tab lands on Back/Next immediately,
 * and Escape closes the tour.
 */
@Component({
  selector: 'tour-card',
  template: `
    <div
      #card
      *ngIf="guide.currentStep$ | async as active"
      class="tour-card e11-fixed e11-z-[1001] e11-w-[400px] e11-max-w-[calc(100vw-2rem)] e11-rounded-lg e11-shadow-2xl e11-ring-2 e11-ring-skin-primary-accent/50 e11-border e11-border-skin-app-borders e11-flex e11-flex-col e11-overflow-hidden"
      [style.left.px]="posX"
      [style.top.px]="posY"
      [style.right.px]="posX === null ? 24 : null"
      [style.bottom.px]="posY === null ? 24 : null"
      role="dialog"
      aria-modal="false"
      tabindex="-1"
      [attr.aria-label]="active.step.title | translate"
      data-tour-card
    >
      <!-- Header = drag handle + (category eyebrow) + title + close. Accent tint helps it stand out. -->
      <div
        class="tour-card-handle e11-flex e11-items-start e11-gap-2 e11-px-4 e11-py-3 e11-cursor-move e11-select-none e11-bg-skin-primary/5 e11-border-b e11-border-skin-app-borders/60"
        (pointerdown)="startDrag($event)"
        data-tour-card-handle
      >
        <span class="material-icons-outlined md-20 e11-text-skin-primary-accent">explore</span>
        <div class="e11-flex e11-flex-col e11-flex-1 e11-min-w-0">
          <span
            *ngIf="categoryFor(active.step) as category"
            class="e11-text-[11px] e11-uppercase e11-tracking-wide e11-font-primary-demibold e11-text-skin-primary-accent e11-leading-none"
            data-tour-card-category
            >{{ category | translate }}</span
          >
          <h4 class="e11-mb-0 e11-font-primary-demibold e11-text-skin-base e11-line-clamp-1">{{ active.step.title | translate }}</h4>
        </div>
        <!-- Quiet grey dismiss, tucked into the corner — not a control competing with Back/Next. Keeps a
             44px-ish hit area and a visible focus ring; stops its own pointerdown so grabbing it does
             not start a drag of the card. -->
        <button
          type="button"
          class="tour-card-close e11-shrink-0 e11-flex e11-items-center e11-justify-center e11-w-7 e11-h-7 -e11-mt-0.5 -e11-mr-1 e11-rounded e11-bg-transparent e11-border-0 e11-cursor-pointer e11-text-skin-light hover:e11-text-skin-base hover:e11-bg-skin-grey/10 focus-visible:e11-outline focus-visible:e11-outline-2 focus-visible:e11-outline-skin-primary-accent"
          [title]="'Close' | translate"
          [attr.aria-label]="'Close' | translate"
          (pointerdown)="$event.stopPropagation()"
          (click)="close()"
          data-tour-card-close
        >
          <span class="material-icons-outlined md-18">close</span>
        </button>
      </div>

      <!-- Body prose -->
      <div class="e11-px-5 e11-py-4">
        <p class="e11-mb-0 e11-text-sm e11-text-skin-base">{{ active.step.body | translate }}</p>
      </div>

      <!-- Footer: Back / step dots / Next (or Done).
           The buttons never shrink and the dot row is the flexible element, which is what keeps the
           primary button on screen. An e11-button carries 2rem of padding on EACH side, so Back and
           Next cost ~100px apiece before their label; with the dots as a rigid third child the row
           outgrew the 360px of usable card width once the walk reached 11 steps, and the card's
           overflow-hidden clipped Next/Done off the right edge. Now the dots take what is left,
           centred, and wrap onto a second line rather than pushing anything out — so the footer holds
           at any step count and at the narrow max-width the card falls back to on a phone. -->
      <div class="e11-flex e11-items-center e11-justify-between e11-gap-3 e11-px-5 e11-pb-5">
        <e11-button
          *ngIf="!active.step.intro"
          class="e11-shrink-0"
          [color]="'secondary'"
          [ghost]="true"
          [value]="'Back' | translate"
          [disabled]="active.index === 0"
          (click)="back()"
          data-tour-card-back
        ></e11-button>
        <span *ngIf="active.step.intro"></span>

        <div
          class="e11-flex e11-flex-1 e11-flex-wrap e11-min-w-0 e11-items-center e11-justify-center e11-gap-1"
          aria-hidden="true"
          data-tour-card-dots
        >
          <span
            *ngFor="let dot of dots(active.total); let i = index"
            class="e11-w-2 e11-h-2 e11-shrink-0 e11-rounded-full"
            [ngClass]="i === active.index ? 'e11-bg-skin-primary-accent' : 'e11-bg-skin-app-borders'"
          ></span>
        </div>

        <e11-button
          class="e11-shrink-0"
          [color]="'primary'"
          [value]="(active.step.intro ? 'Start tour' : isLast(active) ? 'Done' : 'Next') | translate"
          (click)="advance(active)"
          data-tour-card-next
        ></e11-button>
      </div>
    </div>
  `,
  styles: [
    `
      .tour-card {
        background: rgba(255, 255, 255, 0.97);
        -webkit-backdrop-filter: blur(10px);
        backdrop-filter: blur(10px);
      }
      .tour-card:focus {
        outline: none;
      }
      .tour-card-handle {
        touch-action: none;
      }
    `,
  ],
})
export class TourCardComponent implements OnDestroy {
  @ViewChild('card') cardRef?: ElementRef<HTMLElement>

  /** Fixed-position offsets; `null` = the default bottom-right anchor, which is where every step sits
   *  until the user drags the card somewhere else. */
  posX: number | null = null
  posY: number | null = null

  /** Latched so Escape only closes while a walk is actually running. */
  private active = false

  private dragging = false
  private startX = 0
  private startY = 0
  private originLeft = 0
  private originTop = 0
  /** Drives the eyebrow's narrow-viewport wording; re-read on resize. */
  private viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1440
  private readonly subs = new Subscription()

  private readonly onMove = (event: PointerEvent): void => {
    if (!this.dragging) return
    this.posX = this.clampX(this.originLeft + (event.clientX - this.startX))
    this.posY = this.clampY(this.originTop + (event.clientY - this.startY))
  }
  private readonly onUp = (): void => {
    this.dragging = false
  }

  constructor(public guide: GuideService) {
    document.addEventListener('pointermove', this.onMove)
    document.addEventListener('pointerup', this.onUp)
    this.subs.add(
      this.guide.active$.subscribe(active => {
        this.active = active
        // Each fresh walk starts back at the default corner, whatever the last one was dragged to.
        if (active) {
          this.posX = null
          this.posY = null
        }
      })
    )
    // Take focus as each step arrives so the walk is operable from the keyboard alone.
    this.subs.add(this.guide.currentStep$.subscribe(step => step && this.focusCard()))
  }

  ngOnDestroy(): void {
    document.removeEventListener('pointermove', this.onMove)
    document.removeEventListener('pointerup', this.onUp)
    this.subs.unsubscribe()
  }

  /** The eyebrow for this step: "Left navigation" becomes "Main menu" once the rail is behind the
   *  hamburger, so the card never names a place the user cannot see. */
  categoryFor(step: IGuideStep): string | undefined {
    return resolveTourTarget(step, this.viewportWidth).category
  }

  @HostListener('window:resize')
  onResize(): void {
    this.viewportWidth = typeof window !== 'undefined' ? window.innerWidth : this.viewportWidth
  }

  /** Escape leaves the tour — the keyboard equivalent of the card's close button. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.active) this.close()
  }

  /** Begin dragging from the header: seed the offsets from the card's current on-screen position. */
  startDrag(event: PointerEvent): void {
    const rect = this.cardRef?.nativeElement.getBoundingClientRect()
    if (rect) {
      this.originLeft = rect.left
      this.originTop = rect.top
    }
    this.startX = event.clientX
    this.startY = event.clientY
    this.dragging = true
    event.preventDefault()
  }

  dots(total: number): number[] {
    return Array.from({ length: total }, (_, i) => i)
  }

  isLast(active: { index: number; total: number }): boolean {
    return active.index === active.total - 1
  }

  /** Next on any middle step; Done on the last step ends the walk. */
  advance(active: { index: number; total: number }): void {
    if (this.isLast(active)) {
      this.guide.stop()
      return
    }
    void this.guide.next()
  }

  back(): void {
    void this.guide.back()
  }

  close(): void {
    this.guide.stop()
  }

  /** Deferred a tick so the card exists in the DOM on the step that first renders it. */
  private focusCard(): void {
    setTimeout(() => this.cardRef?.nativeElement.focus({ preventScroll: true }))
  }

  private clampX(x: number): number {
    const w = this.cardRef?.nativeElement.offsetWidth ?? 400
    return Math.max(0, Math.min((typeof window !== 'undefined' ? window.innerWidth : w) - w, x))
  }

  private clampY(y: number): number {
    const h = this.cardRef?.nativeElement.offsetHeight ?? 200
    return Math.max(0, Math.min((typeof window !== 'undefined' ? window.innerHeight : h) - h, y))
  }
}
